import {
  MAX_CHAT_UPLOAD_FILES,
  MAX_CHAT_UPLOAD_SIZE_BYTES,
  MAX_CHAT_UPLOAD_TOTAL_BYTES,
} from "@hightouch/lib/llm/upload-limits";
import pluralize from "pluralize";
import {
  assertFilesAreNotBlockedTypes,
  type ComposerFile,
} from "src/components/llm/utils/file-processing";

export const base64ByteLength = (base64Data: string): number => {
  const padding = base64Data.endsWith("==")
    ? 2
    : base64Data.endsWith("=")
      ? 1
      : 0;
  return Math.floor((base64Data.length * 3) / 4) - padding;
};

export const attachmentSize = (file: ComposerFile): number | undefined => {
  if (file.sizeBytes !== undefined) return file.sizeBytes;
  if (file.file) return file.file.size;
  if (file.base64Data) return base64ByteLength(file.base64Data);
  return undefined;
};

export function validateAttachmentSelection(
  existing: ComposerFile[],
  incoming: ComposerFile[],
  maxFiles = MAX_CHAT_UPLOAD_FILES,
): void {
  const count = existing.length + incoming.length;
  if (count > maxFiles) {
    const selected = `You selected ${pluralize("file", incoming.length, true)}`;
    const alreadyAttached =
      existing.length > 0
        ? `, and ${existing.length} ${existing.length === 1 ? "is" : "are"} already attached`
        : "";
    throw new Error(
      `${selected}${alreadyAttached}. You can attach up to ${maxFiles} files per message.`,
    );
  }
  assertFilesAreNotBlockedTypes(
    incoming.map((file) => ({ name: file.fileName })),
  );
  for (const file of incoming) {
    const size = attachmentSize(file);
    if (size !== undefined && size > MAX_CHAT_UPLOAD_SIZE_BYTES) {
      throw new Error(
        `"${file.fileName}" exceeds the 1 GiB file limit. Choose a smaller file.`,
      );
    }
  }
  const total = [...existing, ...incoming].reduce(
    (bytes, file) => bytes + (attachmentSize(file) ?? 0),
    0,
  );
  if (total > MAX_CHAT_UPLOAD_TOTAL_BYTES) {
    throw new Error(
      `These files would bring the total to ${(total / 1024 ** 3).toFixed(2)} GiB. Remove files to stay within 2 GiB per message.`,
    );
  }
}
